import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Sparkles, Mic, Search } from "lucide-react";
import { Overline } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = ["inquiries", "waitlist", "shop", "orders", "studio"];
const COPY_KINDS = [
  { value: "lookbook", label: "Lookbook Text" },
  { value: "video_script", label: "Video Script" },
  { value: "media_announcement", label: "Media Announcement" },
];

const inputCls =
  "border border-[#2a2a2e] bg-[#0A0A0B] px-4 py-3 text-sm text-white placeholder:text-stone-600 focus:border-[#D4AF37] focus:outline-none";

const Admin = () => {
  const [key, setKey] = useState(sessionStorage.getItem("av_admin_key") || "");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("inquiries");
  const [inquiries, setInquiries] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [briefs, setBriefs] = useState([]);
  const [edits, setEdits] = useState({});
  const [loading, setLoading] = useState(false);
  const [copyKind, setCopyKind] = useState("lookbook");
  const [copyBrief, setCopyBrief] = useState("");
  const [copyBusy, setCopyBusy] = useState(false);
  const [voiceText, setVoiceText] = useState("");
  const [voiceUrl, setVoiceUrl] = useState("");
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [topic, setTopic] = useState("");
  const [researchBusy, setResearchBusy] = useState(false);
  const fileInputs = useRef({});

  useEffect(() => {
    document.title = "Admin — ADD VALUE HOLDINGS LLC";
    if (sessionStorage.getItem("av_admin_key")) load(sessionStorage.getItem("av_admin_key"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const headers = (k) => ({ "x-admin-key": k });

  const load = async (k) => {
    setLoading(true);
    try {
      const [inq, wait, prods, ords, dr, br] = await Promise.all([
        axios.get(`${API}/contact`, { headers: headers(k) }),
        axios.get(`${API}/waitlist`, { headers: headers(k) }),
        axios.get(`${API}/admin/products`, { headers: headers(k) }),
        axios.get(`${API}/admin/orders`, { headers: headers(k) }),
        axios.get(`${API}/admin/copywriter`, { headers: headers(k) }),
        axios.get(`${API}/admin/research`, { headers: headers(k) }),
      ]);
      setInquiries(inq.data);
      setWaitlist(wait.data);
      setProducts(prods.data);
      setOrders(ords.data);
      setDrafts(dr.data);
      setBriefs(br.data);
      setEdits({});
      setAuthed(true);
      sessionStorage.setItem("av_admin_key", k);
    } catch {
      toast.error("Invalid admin key.");
      setAuthed(false);
      sessionStorage.removeItem("av_admin_key");
    } finally {
      setLoading(false);
    }
  };

  const setEdit = (slug, field, value) =>
    setEdits((e) => ({ ...e, [slug]: { ...e[slug], [field]: value } }));

  const saveProduct = async (slug) => {
    const edit = edits[slug] || {};
    const payload = {};
    if (edit.price !== undefined && edit.price !== "") payload.price = parseFloat(edit.price);
    if (edit.inventory !== undefined && edit.inventory !== "") payload.inventory = parseInt(edit.inventory, 10);
    if (edit.published !== undefined) payload.published = edit.published;
    if (Object.keys(payload).length === 0) return;
    try {
      await axios.patch(`${API}/admin/products/${slug}`, payload, { headers: headers(key) });
      toast.success(`${slug} saved.`);
      load(key);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed.");
    }
  };

  const uploadImage = async (slug, which, file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      await axios.post(`${API}/admin/products/${slug}/image?which=${which}`, form, {
        headers: { ...headers(key), "Content-Type": "multipart/form-data" },
      });
      toast.success(`${which === "front" ? "Image" : "Back image"} updated for ${slug}.`);
      load(key);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed.");
    }
  };

  const generateCopy = async (e) => {
    e.preventDefault();
    setCopyBusy(true);
    try {
      await axios.post(`${API}/admin/copywriter`, { kind: copyKind, brief: copyBrief }, { headers: headers(key) });
      toast.success("Draft ready.");
      setCopyBrief("");
      load(key);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed.");
    } finally {
      setCopyBusy(false);
    }
  };

  const generateVoiceover = async (e) => {
    e.preventDefault();
    setVoiceBusy(true);
    setVoiceUrl("");
    try {
      const res = await axios.post(`${API}/admin/voiceover`, { text: voiceText }, { headers: headers(key) });
      setVoiceUrl(res.data.url);
      toast.success("Voiceover ready.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Voiceover failed.");
    } finally {
      setVoiceBusy(false);
    }
  };

  const runResearch = async (e) => {
    e.preventDefault();
    setResearchBusy(true);
    try {
      await axios.post(`${API}/admin/research`, { topic }, { headers: headers(key) });
      toast.success("Brief ready.");
      setTopic("");
      load(key);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Research failed.");
    } finally {
      setResearchBusy(false);
    }
  };

  const fmt = (d) => new Date(d).toLocaleString();

  const tabLabel = (t) =>
    t === "inquiries" ? `Enquiries (${inquiries.length})`
      : t === "waitlist" ? `Waitlist (${waitlist.length})`
      : t === "shop" ? `Shop (${products.length})`
      : t === "orders" ? `Orders (${orders.length})`
      : "Studio";

  const studioCard = "border border-[#222226] bg-[#121214] p-8";
  const studioTitle = "font-serif text-2xl text-white";

  return (
    <div data-testid="admin-page" className="mx-auto min-h-screen max-w-7xl px-6 pb-24 pt-40 sm:px-8 lg:px-12">
      <Overline>Internal</Overline>
      <h1 className="mt-6 font-serif text-3xl tracking-tight text-white sm:text-4xl">
        Enquiries, Shop & Studio
      </h1>

      {!authed ? (
        <form
          data-testid="admin-login-form"
          onSubmit={(e) => {
            e.preventDefault();
            load(key);
          }}
          className="mt-12 flex max-w-md flex-col gap-4"
        >
          <input
            data-testid="admin-key-input"
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="Admin key"
            className={inputCls}
          />
          <button
            data-testid="admin-login-button"
            type="submit"
            disabled={loading}
            className="bg-white px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-black transition-colors duration-300 hover:bg-[#D4AF37] disabled:opacity-50"
          >
            {loading ? "Loading…" : "Sign In"}
          </button>
        </form>
      ) : (
        <div className="mt-12">
          <div className="flex flex-wrap gap-4 border-b border-[#222226]">
            {TABS.map((t) => (
              <button
                key={t}
                data-testid={`admin-tab-${t}`}
                onClick={() => setTab(t)}
                className={`-mb-px border-b px-2 pb-4 font-mono text-[11px] uppercase tracking-[0.25em] transition-colors duration-300 ${
                  tab === t ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-stone-500 hover:text-white"
                }`}
              >
                {tabLabel(t)}
              </button>
            ))}
          </div>

          {tab === "inquiries" && (
            <div data-testid="admin-inquiries-list" className="mt-8 space-y-4">
              {inquiries.length === 0 && <p className="text-sm text-stone-500">No enquiries yet.</p>}
              {inquiries.map((m) => (
                <div key={m.id} className="border border-[#222226] bg-[#121214] p-6">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <p className="font-serif text-xl text-white">
                      {m.name} <span className="text-sm text-stone-500">— {m.reason}</span>
                    </p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-600">{fmt(m.created_at)}</p>
                  </div>
                  <p className="mt-2 font-mono text-xs text-[#D4AF37]">{m.email}{m.phone ? ` · ${m.phone}` : ""}{m.company ? ` · ${m.company}` : ""}</p>
                  <p className="mt-4 text-sm font-light leading-relaxed text-stone-400">{m.message}</p>
                </div>
              ))}
            </div>
          )}

          {tab === "waitlist" && (
            <div data-testid="admin-waitlist-list" className="mt-8 space-y-2">
              {waitlist.length === 0 && <p className="text-sm text-stone-500">No waitlist signups yet.</p>}
              {waitlist.map((w) => (
                <div key={w.id} className="flex items-center justify-between border border-[#222226] bg-[#121214] px-6 py-4">
                  <p className="text-sm text-stone-300">{w.email}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-600">{fmt(w.created_at)}</p>
                </div>
              ))}
            </div>
          )}

          {tab === "shop" && (
            <div data-testid="admin-shop-list" className="mt-8 space-y-4">
              <p className="max-w-2xl text-sm font-light text-stone-500">
                Set a price and inventory, then publish. Publishing syncs the
                price with Stripe and makes the piece purchasable — the SHOP
                button appears in the site navigation automatically once any
                piece is live. Upload your own photography to replace any image.
              </p>
              {products.map((p) => {
                const edit = edits[p.slug] || {};
                const priceVal = edit.price !== undefined ? edit.price : (p.price ?? "");
                const invVal = edit.inventory !== undefined ? edit.inventory : p.inventory;
                const pubVal = edit.published !== undefined ? edit.published : p.published;
                return (
                  <div key={p.slug} data-testid={`admin-product-${p.slug}`} className="border border-[#222226] bg-[#121214] p-6">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div className="flex items-center gap-5">
                        <img src={p.image} alt={p.name} className="h-16 w-12 object-cover grayscale" />
                        <div>
                          <p className="font-serif text-xl text-white">{p.name}</p>
                          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500">
                            {p.category} · {p.slug}
                          </p>
                        </div>
                      </div>
                      <span className={`border px-3 py-1 font-mono text-[9px] uppercase tracking-[0.25em] ${pubVal ? "border-[#D4AF37] text-[#D4AF37]" : "border-[#2a2a2e] text-stone-500"}`}>
                        {pubVal ? "Live" : "Unpublished"}
                      </span>
                    </div>
                    <div className="mt-6 flex flex-wrap items-end gap-4">
                      <div>
                        <label className="mb-2 block font-mono text-[9px] uppercase tracking-[0.25em] text-stone-500">Price (USD)</label>
                        <input
                          data-testid={`admin-price-${p.slug}`}
                          type="number"
                          min="0"
                          step="0.01"
                          value={priceVal}
                          onChange={(e) => setEdit(p.slug, "price", e.target.value)}
                          placeholder="Not set"
                          className={`w-32 ${inputCls}`}
                        />
                      </div>
                      <div>
                        <label className="mb-2 block font-mono text-[9px] uppercase tracking-[0.25em] text-stone-500">Inventory</label>
                        <input
                          data-testid={`admin-inventory-${p.slug}`}
                          type="number"
                          min="0"
                          value={invVal}
                          onChange={(e) => setEdit(p.slug, "inventory", e.target.value)}
                          className={`w-28 ${inputCls}`}
                        />
                      </div>
                      <label className="flex cursor-pointer items-center gap-3 border border-[#2a2a2e] px-4 py-3">
                        <input
                          data-testid={`admin-publish-${p.slug}`}
                          type="checkbox"
                          checked={!!pubVal}
                          onChange={(e) => setEdit(p.slug, "published", e.target.checked)}
                          className="h-4 w-4 accent-[#D4AF37]"
                        />
                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone-300">Published</span>
                      </label>
                      <button
                        data-testid={`admin-save-${p.slug}`}
                        onClick={() => saveProduct(p.slug)}
                        className="bg-white px-6 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-black transition-colors duration-300 hover:bg-[#D4AF37]"
                      >
                        Save
                      </button>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        ref={(el) => (fileInputs.current[`${p.slug}-front`] = el)}
                        onChange={(e) => uploadImage(p.slug, "front", e.target.files?.[0])}
                      />
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        ref={(el) => (fileInputs.current[`${p.slug}-back`] = el)}
                        onChange={(e) => uploadImage(p.slug, "back", e.target.files?.[0])}
                      />
                      <button
                        data-testid={`admin-upload-front-${p.slug}`}
                        onClick={() => fileInputs.current[`${p.slug}-front`]?.click()}
                        className="inline-flex items-center gap-2 border border-[#2a2a2e] px-4 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-stone-300 transition-colors duration-300 hover:border-[#D4AF37] hover:text-[#D4AF37]"
                      >
                        <Upload className="h-3.5 w-3.5" /> Image
                      </button>
                      <button
                        data-testid={`admin-upload-back-${p.slug}`}
                        onClick={() => fileInputs.current[`${p.slug}-back`]?.click()}
                        className="inline-flex items-center gap-2 border border-[#2a2a2e] px-4 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-stone-300 transition-colors duration-300 hover:border-[#D4AF37] hover:text-[#D4AF37]"
                      >
                        <Upload className="h-3.5 w-3.5" /> Back
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {tab === "orders" && (
            <div data-testid="admin-orders-list" className="mt-8 space-y-2">
              {orders.length === 0 && <p className="text-sm text-stone-500">No orders yet.</p>}
              {orders.map((o) => (
                <div key={o.session_id} className="flex flex-wrap items-center justify-between gap-3 border border-[#222226] bg-[#121214] px-6 py-4">
                  <div>
                    <p className="text-sm text-white">
                      {o.product_name} <span className="text-stone-500">· {o.size} · x{o.quantity}</span>
                    </p>
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-stone-600">
                      {fmt(o.created_at)} · {(o.amount / 100).toFixed(2)} {String(o.currency).toUpperCase()}
                    </p>
                  </div>
                  <span className={`border px-3 py-1 font-mono text-[9px] uppercase tracking-[0.25em] ${o.payment_status === "paid" ? "border-[#D4AF37] text-[#D4AF37]" : "border-[#2a2a2e] text-stone-500"}`}>
                    {o.payment_status}
                  </span>
                </div>
              ))}
            </div>
          )}

          {tab === "studio" && (
            <div data-testid="admin-studio" className="mt-8 grid gap-6 lg:grid-cols-2">
              <div className={studioCard}>
                <div className="flex items-center gap-3">
                  <Sparkles className="h-5 w-5 text-[#D4AF37]" />
                  <h2 className={studioTitle}>Copywriter</h2>
                </div>
                <p className="mt-3 text-sm font-light text-stone-500">
                  Drafts luxury copy for the lookbook, video scripts, and media announcements. Nothing is published automatically.
                </p>
                <form onSubmit={generateCopy} className="mt-6 space-y-4">
                  <select
                    data-testid="copywriter-kind"
                    value={copyKind}
                    onChange={(e) => setCopyKind(e.target.value)}
                    className={`w-full appearance-none ${inputCls}`}
                  >
                    {COPY_KINDS.map((k) => (
                      <option key={k.value} value={k.value} className="bg-[#121214]">{k.label}</option>
                    ))}
                  </select>
                  <textarea
                    data-testid="copywriter-brief"
                    value={copyBrief}
                    onChange={(e) => setCopyBrief(e.target.value)}
                    placeholder="Brief — e.g. Lookbook intro for Collection 001, monochrome tailoring"
                    rows={3}
                    required
                    className={`w-full resize-none ${inputCls}`}
                  />
                  <button
                    data-testid="copywriter-generate"
                    type="submit"
                    disabled={copyBusy}
                    className="bg-white px-6 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-black transition-colors duration-300 hover:bg-[#D4AF37] disabled:opacity-50"
                  >
                    {copyBusy ? "Writing…" : "Generate Draft"}
                  </button>
                </form>
                <div className="mt-8 space-y-4">
                  {drafts.map((d) => (
                    <div key={d.id} className="border border-[#222226] bg-[#0A0A0B] p-5">
                      <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#D4AF37]">
                        {d.kind.replace(/_/g, " ")} · {fmt(d.created_at)}
                      </p>
                      <p className="mt-2 text-sm italic text-stone-500">“{d.brief}”</p>
                      <p className="mt-3 whitespace-pre-wrap text-sm font-light leading-relaxed text-stone-300">{d.draft}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-6">
                <div className={studioCard}>
                  <div className="flex items-center gap-3">
                    <Mic className="h-5 w-5 text-[#D4AF37]" />
                    <h2 className={studioTitle}>Voiceover</h2>
                  </div>
                  <p className="mt-3 text-sm font-light text-stone-500">
                    Studio voiceovers for future video and media releases. Requires your ElevenLabs key.
                  </p>
                  <form onSubmit={generateVoiceover} className="mt-6 space-y-4">
                    <textarea
                      data-testid="voiceover-text"
                      value={voiceText}
                      onChange={(e) => setVoiceText(e.target.value)}
                      placeholder="Script for the voiceover…"
                      rows={3}
                      required
                      className={`w-full resize-none ${inputCls}`}
                    />
                    <button
                      data-testid="voiceover-generate"
                      type="submit"
                      disabled={voiceBusy}
                      className="bg-white px-6 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-black transition-colors duration-300 hover:bg-[#D4AF37] disabled:opacity-50"
                    >
                      {voiceBusy ? "Generating…" : "Generate Voiceover"}
                    </button>
                  </form>
                  {voiceUrl && (
                    <audio data-testid="voiceover-player" controls src={voiceUrl} className="mt-6 w-full" />
                  )}
                </div>

                <div className={studioCard}>
                  <div className="flex items-center gap-3">
                    <Search className="h-5 w-5 text-[#D4AF37]" />
                    <h2 className={studioTitle}>Research</h2>
                  </div>
                  <p className="mt-3 text-sm font-light text-stone-500">
                    Web-grounded market and trend briefs for Business & Ventures. Requires your Perplexity key.
                  </p>
                  <form onSubmit={runResearch} className="mt-6 flex gap-3">
                    <input
                      data-testid="research-topic"
                      value={topic}
                      onChange={(e) => setTopic(e.target.value)}
                      placeholder="Topic — e.g. luxury streetwear market 2026"
                      required
                      className={`w-full ${inputCls}`}
                    />
                    <button
                      data-testid="research-generate"
                      type="submit"
                      disabled={researchBusy}
                      className="shrink-0 bg-white px-6 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-black transition-colors duration-300 hover:bg-[#D4AF37] disabled:opacity-50"
                    >
                      {researchBusy ? "…" : "Brief Me"}
                    </button>
                  </form>
                  <div className="mt-6 space-y-4">
                    {briefs.map((b) => (
                      <div key={b.id} className="border border-[#222226] bg-[#0A0A0B] p-5">
                        <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#D4AF37]">
                          {fmt(b.created_at)}
                        </p>
                        <p className="mt-2 font-serif text-lg text-white">{b.topic}</p>
                        <p className="mt-3 whitespace-pre-wrap text-sm font-light leading-relaxed text-stone-300">{b.brief}</p>
                        {b.citations?.length > 0 && (
                          <div className="mt-4 border-t border-[#222226] pt-3">
                            {b.citations.slice(0, 6).map((c) => (
                              <a key={c} href={c} target="_blank" rel="noopener noreferrer" className="block truncate text-xs text-stone-500 transition-colors duration-300 hover:text-stone-300">
                                {c}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Admin;
